import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DeviceMeasurement} from "../../../../../model/device-measurement";
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {DeviceExpressionChecker} from "../../../../../model/device-expression-checker";
import {PreviousRouteService} from "../../../../../services/previous-route.service";
import {DevicesService} from "../../../../../services/devices.service";
import {MessageService} from "primeng/api";
import {noSpecialCharacters} from "../../../../../shared/custom-validators";
import {translate} from "@ngneat/transloco";
import {DefaultMeasurementType} from "../../../../../model/default-measurement-type";
import {DefaultMeasurementTypesService} from "../../../../../services/default-measurement-types.service";

@Component({
  selector: 'app-default-devices-form-expressions',
  templateUrl: './default-devices-form-expressions.component.html',
  styleUrls: ['./default-devices-form-expressions.component.scss']
})
export class DefaultDevicesFormExpressionsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() deviceExpressions: DeviceMeasurement[]
  @Output() deviceExpressionsChange = new EventEmitter<DeviceMeasurement[]>();
  @Output() previousStep = new EventEmitter<void>();
  @Output() nextStep = new EventEmitter<void>();
  @Output() cancel = new EventEmitter<string>();

  measurementExpressionForm: FormGroup;
  rowsForm: FormArray;

  measurements: DeviceMeasurement[];
  expressions: DeviceMeasurement[];

  deviceExpressionChecker: DeviceExpressionChecker;

  defaultMeasurementTypes: DefaultMeasurementType[];
  rowEdit: boolean;
  rowEditIndex: number;

  constructor(private previousService: PreviousRouteService,
              private defaultMeasurementTypesService: DefaultMeasurementTypesService,
              private formBuilder: FormBuilder,
              private devicesService: DevicesService,
              private messageService: MessageService) {
    this.measurements = [];
    this.expressions = [];
    if (!this.deviceExpressions) this.deviceExpressions = [];
    this.rowsForm = this.formBuilder.array([]);
    this.measurementExpressionForm = this.createExpressionForm();
  }

  ngOnInit(): void {
    this.loadAndCreateForm();
  }

  private loadAndCreateForm(): void {
    this.defaultMeasurementTypesService.getDefaultMeasurementTypes().subscribe(response => {
      this.defaultMeasurementTypes = response.defaultMeasurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.measurements = this.deviceMeasurements;
      if (this.deviceExpressions.length > 0) {
        this.rowsForm = this.formBuilder.array(
          this.deviceExpressions.map(measurement => this.addMeasurementForm(measurement))
        );
      }
    });
  }

  private createExpressionForm(): FormGroup {
    const form = this.formBuilder.group({
      name: this.formBuilder.control(null,
        [Validators.maxLength(255),
          Validators.required,
          noSpecialCharacters]),
      description: this.formBuilder.control(null,
        [Validators.maxLength(255)]),
      alias: this.formBuilder.control(null),
      measurementType: this.formBuilder.control(null, Validators.required),
      expression: this.formBuilder.control(null, Validators.required),
      uuid: this.formBuilder.control(null)
    }, {updateOn: 'blur'})
    return form;
  }

  checkExpression(editing?: boolean): void {
    const measurements = [];
    let expression;
    let newMeasurement;
    this.measurements.forEach(measurement => measurements.push(measurement.name));

    expression = this.measurementExpressionForm.get('expression').value;
    newMeasurement = this.measurementExpressionForm;

    this.deviceExpressionChecker = {variableSensors: measurements, expression: expression};
    this.devicesService.checkExpression(this.deviceExpressionChecker).toPromise().then((response) => {
      const valid = response.validExpression;
      if (valid === true) {
        if (!editing) {
          this.rowsForm.push(newMeasurement);
          this.deviceExpressions.push(newMeasurement.value);
          this.measurementExpressionForm = this.createExpressionForm();
        } else {
          this.rowsForm.at(this.rowEditIndex).setValue(this.measurementExpressionForm.value);
          this.deviceExpressions = this.rowsForm.value;
          this.measurementExpressionForm = this.createExpressionForm();
          this.rowEditIndex = null;
          this.rowEdit = false;
        }
      } else {
        const summary = translate('error.edit.expression');
        const detail = translate('error.jexlExpression.expressionErrors');
        this.messageService.add({severity: 'error', summary, detail, sticky: true});
      }
    }, error => {
      const summary = translate('error.edit.expression');
      const detail = translate('error.edit.expressionDetail');
      this.messageService.add({severity: 'error', summary, detail, sticky: true});
    });
  }

  addSensor(sensorName: string): void {
    let expression = this.measurementExpressionForm.get('expression').value ?? '';
    expression += sensorName;
    this.measurementExpressionForm.get('expression').setValue(expression);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
    this.deviceExpressions = this.rowsForm.value;
  }

  onRowEdit(rowIndex: number): void {
    this.measurementExpressionForm.setValue(this.rowsForm.at(rowIndex).value);
    this.rowEditIndex = rowIndex;
    this.rowEdit = true;
  }

  private addMeasurementForm(measurement: DeviceMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [measurement.description, [Validators.maxLength(255)]],
      alias: [measurement.alias],
      measurementType: [measurement.measurementType, [Validators.required]],
      expression: [measurement.expression, [Validators.required]],
      uuid: [measurement.uuid]
    }, {updateOn: 'blur'});
  }

  onPreviousStep(): void {
    this.previousStep.emit();
    this.deviceExpressionsChange.emit(this.deviceExpressions);
  }

  onNextStep(): void {
    this.nextStep.emit();
    this.deviceExpressionsChange.emit(this.deviceExpressions);
  }

  onCancel(): void {
    this.cancel.emit(this.previousService.getPreviousUrl());
  }

}
